/**
 * AgentCallsPage — Professional Call Reporting Dashboard.
 *
 * Clean, elegant reporting-style table with export to CSV,
 * all columns visible, proper date/time formatting, and
 * KC3-consistent 3px border radius design.
 */
import { useEffect, useState, useCallback, type JSX } from 'react'
import { Download, Phone, PhoneIncoming, PhoneOutgoing, Clock, Calendar, Hash, AlertCircle, CheckCircle2, XCircle, Play, X } from 'lucide-react'
import { RecordingPlayer } from '../../modules/recordings/RecordingPlayer.js'

import PageHeader from '../../components/admin/PageHeader.js'
import { api } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'
import { formatInTimezone } from '../../utils/timezone.js'

interface CallRow {
  id: string
  uniqueId: string
  fromNumber: string
  toNumber: string
  direction: string
  startTime: string
  durationSec: number | null
  disposition: string | null
  hangupCause: string | null
  /**
   * Set when this call has an ENCRYPTED recording the agent may play.
   *
   * A boolean-ish id rather than the recording row: the row carries
   * `storageKey` and `pciPauseSegments`, and the latter describes exactly when
   * a customer read a card number aloud. `GET /recordings/:id` gates that
   * behind `mayAccessRecording`; returning it in the calls list would route
   * around the check.
   */
  recordingId?: string | null
}

interface CallsResponse {
  calls: CallRow[]
  total: number
  limit: number
  offset: number
}

function formatDuration(totalSec: number | null): string {
  if (totalSec === null || Number.isNaN(totalSec)) return '—'
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return formatInTimezone(d, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }, 'en-GB')
}

function getDirectionBadge(direction: string) {
  const upper = direction.toUpperCase()
  if (upper === 'INBOUND') {
    return (
      <span className="inline-flex items-center gap-1 rounded-[3px] bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
        <PhoneIncoming className="h-3 w-3" />
        Inbound
      </span>
    )
  }
  if (upper === 'OUTBOUND') {
    return (
      <span className="inline-flex items-center gap-1 rounded-[3px] bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
        <PhoneOutgoing className="h-3 w-3" />
        Outbound
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-[3px] bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
      <Phone className="h-3 w-3" />
      {upper}
    </span>
  )
}

function getDispositionBadge(disposition: string | null) {
  if (!disposition) return <span className="text-slate-400">—</span>
  const upper = disposition.toUpperCase()
  if (upper === 'ANSWERED') {
    return (
      <span className="inline-flex items-center gap-1 rounded-[3px] bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
        <CheckCircle2 className="h-3 w-3" />
        Answered
      </span>
    )
  }
  if (upper === 'FAILED') {
    return (
      <span className="inline-flex items-center gap-1 rounded-[3px] bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
        <XCircle className="h-3 w-3" />
        Failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-[3px] bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
      <AlertCircle className="h-3 w-3" />
      {upper}
    </span>
  )
}

export default function AgentCallsPage(): JSX.Element {
  const agentId = useAuthStore((s) => s.user?.sub ?? null)

  const [rows, setRows] = useState<CallRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * Recording currently open in the player, or null for closed.
   *
   * Holds the recordingId rather than a boolean: `RecordingPlayer` fetches
   * `/recordings/:id/stream` on mount, so unmounting it on close is what stops
   * playback and releases the blob URL — a persistent player kept behind a
   * `hidden` class would keep the decrypted audio alive in the tab.
   */
  const [playingId, setPlayingId] = useState<string | null>(null)

  useEffect(() => {
    if (!agentId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<CallsResponse>(`/calls?agentId=${agentId}&limit=50`)
      .then((res) => {
        if (!cancelled) setRows(res.calls)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load calls')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [agentId])

  const exportCSV = useCallback(() => {
    if (rows.length === 0) return
    const headers = ['Date & Time', 'Direction', 'From', 'To', 'Duration', 'Disposition', 'Hangup Cause']
    const lines = rows.map((r) => [
      formatDateTime(r.startTime),
      r.direction,
      r.fromNumber,
      r.toNumber,
      formatDuration(r.durationSec),
      r.disposition ?? '',
      r.hangupCause ?? '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`))
    const csv = [headers.join(','), ...lines.map((l) => l.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `calls_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }, [rows])

  const totalDuration = rows.reduce((sum, r) => sum + (r.durationSec ?? 0), 0)
  const answeredCount = rows.filter((r) => r.disposition?.toUpperCase() === 'ANSWERED').length
  const failedCount = rows.filter((r) => r.disposition?.toUpperCase() === 'FAILED').length

  return (
    <div className="space-y-4 p-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Call History"
          description="Your recent call activity with full details."
        />
        <button
          onClick={exportCSV}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-[3px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Calls</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{rows.length}</p>
        </div>
        <div className="rounded-[3px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Answered</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{answeredCount}</p>
        </div>
        <div className="rounded-[3px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Failed</p>
          <p className="mt-1 text-2xl font-bold text-red-600">{failedCount}</p>
        </div>
        <div className="rounded-[3px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Talk Time</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{formatDuration(totalDuration)}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-[3px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="rounded-[3px] border border-slate-200 bg-white py-12 text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
          <p className="text-sm text-slate-500">Loading call records…</p>
        </div>
      )}

      {/* Empty */}
      {!loading && rows.length === 0 && (
        <div className="rounded-[3px] border border-slate-200 bg-white py-16 text-center">
          <Phone className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm font-medium text-slate-500">No calls yet</p>
          <p className="mt-1 text-xs text-slate-400">Your call history will appear here once you make or receive calls.</p>
        </div>
      )}

      {/* Data Table */}
      {!loading && rows.length > 0 && (
        <div className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80">
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      Date & Time
                    </span>
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Direction
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <PhoneOutgoing className="h-3.5 w-3.5" />
                      From
                    </span>
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <PhoneIncoming className="h-3.5 w-3.5" />
                      To
                    </span>
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      Duration
                    </span>
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Disposition
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Recording
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Hash className="h-3.5 w-3.5" />
                      Call ID
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {formatDateTime(r.startTime)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {getDirectionBadge(r.direction)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-slate-700">
                      {r.fromNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-slate-700">
                      {r.toNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {formatDuration(r.durationSec)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {getDispositionBadge(r.disposition)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {r.recordingId ? (
                        <button
                          onClick={() => setPlayingId(r.recordingId ?? null)}
                          className="flex items-center gap-1.5 rounded-[3px] border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                          title="Play this call recording"
                        >
                          <Play className="h-3 w-3" />
                          Play
                        </button>
                      ) : (
                        /* Explicit, not blank. A blank cell cannot be told
                           apart from a recording that failed to load, and an
                           agent asked "where is the recording" deserves an
                           answer rather than an empty space. */
                        <span className="text-[11px] text-slate-400" title="No recording for this call">
                          —
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-400" title={r.uniqueId}>
                      {r.uniqueId.length > 20 ? `${r.uniqueId.slice(0, 20)}…` : r.uniqueId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Footer */}
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50/50 px-4 py-2.5 text-xs text-slate-500">
            <span>Showing {rows.length} record{rows.length !== 1 ? 's' : ''}</span>
            <span>Last updated: {formatInTimezone(new Date(), { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }, 'en-GB')}</span>
          </div>
        </div>
      )}

      {playingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Call recording"
        >
          <div className="w-full max-w-lg rounded-[3px] border border-slate-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">
                  Call Recording
                </h3>
                <p className="mt-0.5 text-[12.5px] text-slate-500">
                  Segments paused for PCI compliance are skipped automatically.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPlayingId(null)}
                className="rounded-[3px] p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <RecordingPlayer recordingId={playingId} />
          </div>
        </div>
      )}
    </div>
  )
}
